import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { CourseQuiz } from '../components/CourseQuiz';
import { useCourseContent } from '../hooks/useContentData';
import { GrammatikSection } from '../components/GrammatikSection';
import { ExerciseSection } from '../components/ExerciseSection';
import { CourseModuleRenderer } from '../components/CourseModuleRenderer';
import { InteractiveBookViewer } from '../components/InteractiveBookViewer';
import { A1_1_ANSWERS, A1_1_SECTIONS } from '../data/quizBank';
import '../styles/menschen-course.css';

const BOOKS_A11 = {
  kursbuch: { id: 'menschen_a11_kursbuch', label: 'Kursbuch', labelEn: 'Textbook', url: 'https://educap01.datenbank-bildungsmedien.net/player/dc4dd3045486604922df723c77d01665/edureader/app/pdf/lib/web/viewer.html' },
  arbeitsbuch: { id: 'menschen_a11_arbeitsbuch', label: 'Arbeitsbuch', labelEn: 'Workbook', url: 'https://educap01.datenbank-bildungsmedien.net/player/f8a61de4ceeb0d0412454129f0f1cd6c/edureader/app/pdf/lib/web/viewer.html' },
};

const TABS = [
  { id: 'mod1', label: 'Modul 1', icon: '📘', isHeader: true },
  { id: 'l1', label: 'L1: Begrüßung', icon: '👋' },
  { id: 'l2', label: 'L2: Person & Beruf', icon: '💼' },
  { id: 'l3', label: 'L3: Familie', icon: '👨‍👩‍👧‍👦' },
  { id: 'g1', label: 'Grammatik 1-3', icon: '📐', isGrammatik: true },
  { id: 'mod2', label: 'Modul 2', icon: '📗', isHeader: true },
  { id: 'l4', label: 'L4: Möbel kaufen', icon: '🪑' },
  { id: 'l5', label: 'L5: Im Haus', icon: '🏠' },
  { id: 'l6', label: 'L6: Büro & Internet', icon: '💻' },
  { id: 'g2', label: 'Grammatik 4-6', icon: '📐', isGrammatik: true },
  { id: 'mod3', label: 'Modul 3', icon: '📙', isHeader: true },
  { id: 'l7', label: 'L7: Hobbys', icon: '🎨' },
  { id: 'l8', label: 'L8: Verabredungen', icon: '📅' },
  { id: 'l9', label: 'L9: Essen & Trinken', icon: '🍽️' },
  { id: 'g3', label: 'Grammatik 7-9', icon: '📐', isGrammatik: true },
  { id: 'mod4', label: 'Modul 4', icon: '📕', isHeader: true },
  { id: 'l10', label: 'L10: Reisen', icon: '🚆' },
  { id: 'l11', label: 'L11: Tagesablauf', icon: '☀️' },
  { id: 'l12', label: 'L12: Feste', icon: '🎉' },
  { id: 'g4', label: 'Grammatik 10-12', icon: '📐', isGrammatik: true },
  { id: 'pdf', label: 'Interaktives Lernen', icon: '📖', isPdf: true },
  { id: 'exercises', label: 'Übungen', icon: '🏋️', isExercise: true },
  { id: 'quiz', label: 'Quiz', icon: '✅' },
];

const GRAM_MAP = { g1: ['l1','l2','l3'], g2: ['l4','l5','l6'], g3: ['l7','l8','l9'], g4: ['l10','l11','l12'] };

export default function MenschenA1() {
  const [activeTab, setActiveTab] = useState('l1');
  const { lessons, grammatik: grammatikA1, loading } = useCourseContent('menschen_a1');

  const lessonMap = {
    l1: lessons['lektion1'], l2: lessons['lektion2'], l3: lessons['lektion3'],
    l4: lessons['lektion4'], l5: lessons['lektion5'], l6: lessons['lektion6'],
    l7: lessons['lektion7'], l8: lessons['lektion8'], l9: lessons['lektion9'],
    l10: lessons['lektion10'], l11: lessons['lektion11'], l12: lessons['lektion12'],
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="menschen-a1-page">
      <div className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors" data-testid="back-link">
            <ChevronLeft className="h-5 w-5" /><span className="text-sm">Back</span>
          </Link>
          <h1 className="text-blue-500 font-bold">Menschen A1.1</h1>
          <div className="w-20" />
        </div>
      </div>

      <div className="course-container">
        <div className="course-header">
          <h1 style={{ color: '#3b82f6' }}>Menschen A1.1</h1>
          <p>German for Beginners · <span style={{ color: '#3b82f6' }}>الألمانية للمبتدئين</span></p>
          <div className="modul-badges">
            <span className="modul-badge" style={{ background: 'rgba(59,130,246,0.2)', color: '#60a5fa' }}>Modul 1–4</span>
            <span className="modul-badge" style={{ background: 'rgba(34,197,94,0.2)', color: '#4ade80' }}>12 Lektionen</span>
            <span className="modul-badge" style={{ background: 'rgba(245,158,11,0.2)', color: '#fbbf24' }}>A1.1 Level</span>
          </div>
        </div>

        <div className="tab-nav" data-testid="tab-navigation">
          {TABS.map(tab => tab.isHeader ? (
            <span key={tab.id} className="tab-btn module-header">{tab.icon} {tab.label}</span>
          ) : (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-btn-${tab.id}`}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''} ${tab.isGrammatik ? 'grammatik-tab' : ''} ${tab.isPdf ? 'pdf-tab' : ''} ${tab.isExercise ? 'exercise-tab' : ''}`}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <span className="ml-3 text-slate-400">Loading content...</span>
          </div>
        ) : (
          <>
            {Object.entries(lessonMap).map(([key, data]) => (
              data && <CourseModuleRenderer key={key} tabId={key} moduleData={data} isActive={activeTab === key} accentColor="#3b82f6" />
            ))}

            {Object.entries(GRAM_MAP).map(([gId, lids]) => (
              <div key={gId} className={`section ${activeTab === gId ? 'active' : ''}`} data-testid={`tab-${gId}`}>
                {lids.map(lid => grammatikA1[lid] && <GrammatikSection key={lid} data={grammatikA1[lid]} isActive={activeTab === gId} />)}
              </div>
            ))}

            <InteractiveBookViewer books={BOOKS_A11} courseTitle="Menschen A1.1" isActive={activeTab === 'pdf'} />

            <div className={`section ${activeTab === 'quiz' ? 'active' : ''}`} data-testid="tab-quiz">
              <CourseQuiz quizId="menschen_a1_quiz" courseId="menschen_a1" title="Menschen A1.1 – Abschlussquiz" sections={A1_1_SECTIONS} answers={A1_1_ANSWERS} isActive={true} />
            </div>

            {activeTab === 'exercises' && (
              <div className="section active" data-testid="tab-exercises">
                <ExerciseLessonPicker exerciseKeys={['L1','L2','L3','L4','L5','L6','L7','L8','L9','L10','L11','L12']} labels={['L1: Hallo!','L2: Berufe','L3: Familie','L4: Möbel','L5: Technik','L6: Wohnung','L7: Hobbys','L8: Zeit','L9: Essen','L10: Verkehr','L11: Perfekt','L12: Gesundheit']} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ExerciseLessonPicker({ exerciseKeys, labels }) {
  const [selectedLesson, setSelectedLesson] = useState(exerciseKeys[0]);
  return (
    <div>
      <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '12px' }}>Übungen / Exercises</h2>
      <div className="flex flex-wrap gap-2 mb-4">
        {exerciseKeys.map((key, i) => (
          <button key={key} onClick={() => setSelectedLesson(key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedLesson === key ? 'bg-teal-600 text-white' : 'bg-muted/50 text-muted-foreground hover:bg-muted border border-border'}`}
            data-testid={`exercise-lesson-${key}`}>{labels[i]}</button>
        ))}
      </div>
      <ExerciseSection lessonId={selectedLesson} isActive={true} />
    </div>
  );
}
